import { IBranding } from '@/feed/Feed.interface'
import { getBranding } from '@/api/api'

export const toBase64 = (str: string) =>
  typeof window === 'undefined'
    ? Buffer.from(str).toString('base64')
    : window.btoa(str)

export function getCaseSensitiveParams(
  searchParams: URLSearchParams | string | Record<string, string>
): Record<string, string> {
  let rawSearch = ''

  if (typeof searchParams === 'string') {
    rawSearch = searchParams
  } else if (searchParams instanceof URLSearchParams) {
    rawSearch = searchParams.toString()
  } else if (typeof searchParams === 'object') {
    rawSearch = Object.entries(searchParams)
      .map(([key, value]) => `${key}=${value}`)
      .join('&')
  }

  if (!rawSearch) {
    return {}
  }

  const cleanSearch = rawSearch.startsWith('?')
    ? rawSearch.substring(1)
    : rawSearch

  return Object.fromEntries(
    cleanSearch.split('&').map((param) => {
      const [key, value] = param.split('=')
      return [key, decodeURIComponent(value || '')]
    })
  )
}

export const shimmerEffectSvg = (w: number, h: number) => `
    <svg width="${w}" height="${h}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      <defs>
        <linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style="stop-color: #e4e7e9; stop-opacity: 1" />
          <stop offset="50%" style="stop-color: #d3d6d8; stop-opacity: 1" />
          <stop offset="100%" style="stop-color: #e4e7e9; stop-opacity: 1" />
        </linearGradient>
      </defs>
      <rect width="${w}" height="${h}" fill="#E4E8EA" />
      <rect id="r" width="200" height="${h}" fill="url(#g)" />
      <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite"  />
    </svg>`

export const timeAgo = (timestamp: number): string => {
  let time = timestamp

  // Ensure timestamp is in milliseconds
  if (timestamp.toString().length === 10) {
    time *= 1000
  }

  const now = Date.now()
  const diffInSeconds = Math.floor((now - time) / 1000)

  const intervals: [string, number][] = [
    ['year', 365 * 24 * 60 * 60],
    ['month', 30 * 24 * 60 * 60],
    ['week', 7 * 24 * 60 * 60],
    ['day', 24 * 60 * 60],
    ['hour', 60 * 60],
    ['minute', 60],
    ['second', 1]
  ]

  const result = intervals.find(([, seconds]) => diffInSeconds >= seconds)

  if (result) {
    const [unit, seconds] = result
    const count = Math.floor(diffInSeconds / seconds)
    return `${count} ${unit}${count > 1 ? 's' : ''} ago`
  }

  return 'Now'
}

export async function retryBranding(
  brandingParams: {
    params: { alias: string }
    key: string
    additionalHeaders: { source: string }
  },
  maxRetries: number = 3
): Promise<IBranding | null> {
  // eslint-disable-next-line no-plusplus
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const response = await getBranding(brandingParams)
      return response.data
    } catch (error: unknown) {
      const isLastAttempt = attempt === maxRetries

      if (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error as any)?.response?.data?.data?.error ===
        'Branding feature is not enabled for this app'
      ) {
        return null
      }

      if (isLastAttempt) {
        // eslint-disable-next-line no-console
        console.error(
          `Branding call failed after ${maxRetries} attempts:`,
          error
        )
        return null
      }

      // eslint-disable-next-line no-await-in-loop, no-promise-executor-return
      await new Promise((resolve) => setTimeout(resolve, 1000 * attempt))
    }
  }
  return null
}
